import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import { StatusTimeline, StatusTimelineEntry, StatusTimelineViewedEvent } from '../../types/statusTimeline';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import styles from './StatusTimelineDrawer.module.scss';
import ClockIcon from '../../assets/Svg/ClockIcon';

dayjs.extend(relativeTime);
dayjs.extend(duration);

interface StatusTimelineDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    statusTimeline: StatusTimeline | null;
    loading?: boolean;
}

const StatusTimelineDrawer: React.FC<StatusTimelineDrawerProps> = ({ isOpen, onClose, statusTimeline, loading = false }) => {
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const userList = useAppSelector((state) => state.investor.ownerDropdownOptions);

    const fetchUserName = (userId: string): string => {
        if (!userId || userList.length === 0) return 'SYSTEM';
        const user = userList.find((u) => u.userId === userId);
        return user?.name ?? 'SYSTEM';
    };

    useEffect(() => {
        if (isOpen && statusTimeline) {
            // Track Mixpanel event for timeline viewed
            const mixpanelEvent: StatusTimelineViewedEvent = {
                page: statusTimeline.recordType === 'LEAD' ? 'Leads' : statusTimeline.recordType === 'DEAL' ? 'Deals' : 'Mandate',
                source: userData?.accountType === 'PARTNER' ? 'partner' : 'pno',
                org_id: statusTimeline.recordId,
                user_email: userData?.primaryEmail || '',
                currentUrl: window.location.href,
                pno_id: platformData?.investorOrgId || '',
            };

            Mixpanel.track('status_timeline_viewed', mixpanelEvent);
        }
    }, [isOpen, statusTimeline, userData, platformData]);

    const calculateTimeSpend = (entry: StatusTimelineEntry, nextEntry?: StatusTimelineEntry): string => {
        const entryTime = dayjs(entry.enteredAt);
        const nextEntryTime = nextEntry ? dayjs(nextEntry.enteredAt) : dayjs();
        const duration = dayjs.duration(nextEntryTime.diff(entryTime));

        if (duration.asDays() >= 1) {
            return Math.floor(duration.asDays()) > 1 ? `${Math.floor(duration.asDays())} days` : `${Math.floor(duration.asDays())} day`;
        } else if (duration.asHours() >= 1) {
            return Math.floor(duration.asHours()) > 1
                ? `${Math.floor(duration.asHours())} hours`
                : `${Math.floor(duration.asHours())} hour`;
        } else if (duration.asMinutes() >= 1) {
            return Math.floor(duration.asMinutes()) > 1
                ? `${Math.floor(duration.asMinutes())} minutes`
                : `${Math.floor(duration.asMinutes())} minute`;
        } else if (duration.asSeconds() >= 1) {
            return Math.floor(duration.asSeconds()) > 1
                ? `${Math.floor(duration.asSeconds())} seconds`
                : `${Math.floor(duration.asSeconds())} second`;
        } else {
            return 'just now';
        }
    };

    const getStatusTypeLabel = (recordType: string): string => {
        switch (recordType) {
            case 'LEAD':
                return 'Lead Status';
            case 'DEAL':
                return 'Deal Status';
            case 'MANDATE':
                return 'Mandate Status';
            default:
                return 'Status';
        }
    };

    const renderTimelineEntry = (entry: StatusTimelineEntry, index: number, isLast: boolean) => {
        // Calculate time spent from previous entry
        const nextEntry = !isLast ? statusTimeline?.timeline[index + 1] : undefined;
        const timeSpent = nextEntry ? calculateTimeSpend(entry, nextEntry) : calculateTimeSpend(entry);
        const formattedTime = dayjs(entry.enteredAt).format('h:mm a, D MMM YYYY');

        return (
            <div key={`${entry.enteredAt}-${index}`} className={styles.TimelineEntry}>
                <div className={styles.TimelineLeft}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className={styles.StatusBadge} style={{ backgroundColor: entry.statusColor, color: entry.statusLabelColor }}>
                            {entry.statusName}
                        </div>
                        <div className={styles.TimeSpent}>
                            <ClockIcon width="14" height="14" />
                            {timeSpent}
                        </div>
                    </div>
                </div>

                <div className={styles.TimelineRight}>
                    <div className={styles.StatusTime}>
                        {formattedTime} by {fetchUserName(entry.userId ?? '')}
                    </div>
                </div>

                {!isLast && <div className={styles.TimelineConnector} />}
            </div>
        );
    };

    if (loading) {
        return (
            <SideDrawer width="40%" onClose={onClose}>
                <div className={styles.DrawerHeader}>
                    <h2 className={styles.DrawerTitle}>Loading...</h2>
                </div>
                <div className={styles.LoadingContainer}>
                    <div className={styles.LoadingSpinner} />
                </div>
            </SideDrawer>
        );
    }

    if (!statusTimeline) {
        return null;
    }

    return (
        <SideDrawer width="40%" onClose={onClose}>
            <div className={styles.DrawerHeader}>
                <h2 className={styles.DrawerTitle}>{getStatusTypeLabel(statusTimeline.recordType)}</h2>
                <p className={styles.CompanyName}>{statusTimeline.companyName}</p>
            </div>

            <div className={styles.DrawerContent}>
                <div className={styles.Timeline}>
                    {statusTimeline.timeline.map((entry, index) =>
                        renderTimelineEntry(entry, index, index === statusTimeline.timeline.length - 1),
                    )}
                </div>
            </div>
        </SideDrawer>
    );
};

export default StatusTimelineDrawer;
